import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { toast } from 'sonner';

export const WhatsAppPanel = ({ token, apiCall }) => {
  const [status, setStatus] = useState(null);
  const [qr, setQr] = useState(null);
  const [settings, setSettings] = useState({ admin_whatsapp_number: '', broadcast_new_shifts: true, notify_admin: true, compliance_reminders: true });
  const [testPhone, setTestPhone] = useState('');
  const [testMessage, setTestMessage] = useState('Test message from Koloth Care 👋');
  const [sending, setSending] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const s = await apiCall('/whatsapp/status', 'GET', null, token);
      setStatus(s);
      if (!s.connected && !s.service_down) {
        const q = await apiCall('/whatsapp/qr', 'GET', null, token);
        setQr(q.qr);
      } else {
        setQr(null);
      }
    } catch {
      setStatus({ connected: false, service_down: true });
    }
  }, [apiCall, token]);

  useEffect(() => {
    refresh();
    apiCall('/whatsapp/settings', 'GET', null, token).then(setSettings).catch(() => {});
    const interval = setInterval(refresh, 5000);
    return () => clearInterval(interval);
  }, [refresh, apiCall, token]);

  const saveSettings = async () => {
    try {
      await apiCall('/whatsapp/settings', 'PUT', settings, token);
      toast.success('WhatsApp settings saved');
    } catch {
      toast.error('Failed to save settings');
    }
  };

  return (
    <div className="space-y-4" data-testid="whatsapp-panel">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            <span className="text-green-600">WhatsApp Connection</span>
            {status?.connected ? (
              <Badge className="bg-green-600" data-testid="whatsapp-connected-badge">Connected</Badge>
            ) : status?.service_down ? (
              <Badge className="bg-orange-500 text-white">Reconnecting…</Badge>
            ) : (
              <Badge variant="secondary" data-testid="whatsapp-disconnected-badge">Not Connected</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {status?.connected ? (
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-600">
                Linked as <strong>{status.user?.name || status.user?.id || 'business account'}</strong>. Alerts will be sent from this number.
              </p>
              <Button
                variant="outline"
                className="text-red-600 border-red-300"
                data-testid="whatsapp-logout-btn"
                onClick={async () => {
                  if (!window.confirm('Disconnect WhatsApp? You will need to re-scan the QR code.')) return;
                  try {
                    await apiCall('/whatsapp/logout', 'POST', {}, token);
                    toast.success('WhatsApp disconnected');
                    refresh();
                  } catch {
                    toast.error('Failed to disconnect');
                  }
                }}
              >
                Disconnect
              </Button>
            </div>
          ) : qr ? (
            <div className="text-center space-y-3">
              <p className="text-sm font-medium">Scan this QR code with your business WhatsApp:</p>
              <img src={qr} alt="WhatsApp QR" className="mx-auto border rounded-lg w-64 h-64" data-testid="whatsapp-qr-image" />
              <p className="text-xs text-slate-500">
                On your phone: WhatsApp → Settings → Linked Devices → Link a Device
              </p>
            </div>
          ) : status?.service_down ? (
            <div className="text-sm space-y-1">
              <p className="text-orange-600 font-medium">WhatsApp service is starting up…</p>
              <p className="text-slate-500 text-xs">This happens for about a minute after the app wakes up. It reconnects automatically — this page refreshes itself every few seconds.</p>
            </div>
          ) : (
            <p className="text-sm text-slate-500">Waiting for QR code... (refreshes automatically)</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Notification Settings</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="admin-wa-number">Admin WhatsApp Number (for timesheet & self-booking alerts)</Label>
            <Input
              id="admin-wa-number"
              data-testid="admin-whatsapp-number-input"
              placeholder="e.g. 07123456789"
              value={settings.admin_whatsapp_number}
              onChange={(e) => setSettings({ ...settings, admin_whatsapp_number: e.target.value })}
            />
          </div>
          <div className="space-y-3">
            {[
              { key: 'broadcast_new_shifts', label: 'Broadcast new open shifts to all staff' },
              { key: 'notify_admin', label: 'Notify admin of new timesheets & self-bookings' },
              { key: 'compliance_reminders', label: 'Send daily compliance reminders to staff' }
            ].map(({ key, label }) => (
              <div key={key} className="flex items-center justify-between">
                <span className="text-sm">{label}</span>
                <Switch
                  data-testid={`whatsapp-toggle-${key}`}
                  checked={!!settings[key]}
                  onCheckedChange={(v) => setSettings({ ...settings, [key]: v })}
                />
              </div>
            ))}
          </div>
          <Button onClick={saveSettings} data-testid="save-whatsapp-settings-btn">Save Settings</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Send Test Message</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input
              placeholder="Phone number (e.g. 07123456789)"
              data-testid="whatsapp-test-phone-input"
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
            />
            <Textarea
              placeholder="Message"
              data-testid="whatsapp-test-message-input"
              value={testMessage}
              onChange={(e) => setTestMessage(e.target.value)}
              rows={2}
            />
          </div>
          <Button
            disabled={sending || !status?.connected}
            data-testid="whatsapp-send-test-btn"
            onClick={async () => {
              setSending(true);
              try {
                await apiCall('/whatsapp/send', 'POST', { phone_number: testPhone, message: testMessage }, token);
                toast.success('Message sent!');
              } catch (e) {
                toast.error(e.detail || 'Failed to send — is WhatsApp connected?');
              }
              setSending(false);
            }}
          >
            {sending ? 'Sending...' : 'Send Test Message'}
          </Button>
          {!status?.connected && <p className="text-xs text-orange-600">Connect WhatsApp above to send messages.</p>}
        </CardContent>
      </Card>

      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4 text-sm text-slate-700 space-y-1">
          <p className="font-semibold">📱 What staff can do on WhatsApp:</p>
          <p>• Reply <strong>SHIFTS</strong> — see all open shifts</p>
          <p>• Reply <strong>MY SHIFTS</strong> — see their upcoming assigned shifts</p>
          <p>• Reply <strong>HELP</strong> — see all commands</p>
          <p className="text-xs text-slate-500 mt-2">Staff are matched by the phone number in their profile. Automatic alerts: new shifts, shift assignments and compliance reminders.</p>
        </CardContent>
      </Card>
    </div>
  );
};
